import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product, ProductCategory } from '@runninglime/shared';
import { apiGet } from '../../lib/api.ts';
import { formatPrice, categoryLabel } from '../../lib/utils.ts';
import { ProductPlaceholder, LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';

const CATEGORIES: { value: ProductCategory | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'soda', label: 'Soda' },
  { value: 'energy', label: 'Energy' },
  { value: 'brainstorm', label: 'Brainstorm' },
  { value: 'hydrate', label: 'Hydrate' },
  { value: 'cold-brew', label: 'Cold Brew' },
];

export default function Store() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const url = category ? `/products?category=${category}` : '/products';
      const res = await apiGet<{ items: Product[]; total: number }>(url);
      if (res.success && res.data) {
        setProducts(res.data.items);
      }
      setLoading(false);
    };
    load();
  }, [category]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-dark mb-2">B2B Store</h1>
        <p className="text-gray-500">Browse our full range of Runninglime beverages</p>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setCategory(cat.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              category === cat.value
                ? 'bg-lime text-dark shadow-md'
                : 'bg-white text-gray-600 hover:bg-lime/20 border border-gray-200'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : products.length === 0 ? (
        <EmptyState
          icon="🍋"
          title="No products found"
          description="Try a different category filter"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/store/${product.id}`}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group"
            >
              <ProductPlaceholder name={product.name} category={product.category} imageUrl={product.imageUrl} />
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-green bg-green/10 px-2 py-0.5 rounded-full">
                    {categoryLabel(product.category)}
                  </span>
                </div>
                <h3 className="font-semibold text-dark mb-1 group-hover:text-green transition-colors line-clamp-1">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                  {product.description}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm text-gray-400">from</span>
                  <span className="text-lg font-bold text-dark">
                    {formatPrice(Math.min(...product.variants.map((v) => v.pricePerUnit)))}
                  </span>
                  <span className="text-sm text-gray-400">/unit</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
